import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { cur } from '@/lib/currency';
import { errMsg, money } from './salaryApi';

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-border/60 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium text-right" data-testid={testid}>{value || '—'}</span>
  </div>
);

export const ConfirmPaymentDialog = ({ item, onClose, onDone }) => {
  const [step, setStep] = useState(1);
  const [idNumber, setIdNumber] = useState('');
  const [pin, setPin] = useState('');
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setStep(1); setIdNumber(''); setPin(''); setReview(null); }, [item]);
  const base = item ? `/me/payment-confirmations/${item.kind}/${item.id}` : '';

  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${base}/verify-id`, { id_number: idNumber });
      if (!r.data.pin_set) { toast.error('Create your Salary PIN first (card above the list)'); return; }
      setReview(r.data.review); setStep(2);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${base}/confirm`, { id_number: idNumber, pin });
      toast.success(`Confirmed — reference ${r.data.ack_reference}`);
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); setPin(''); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="confirm-payment-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} className="text-teal-600" />Confirm payment received</DialogTitle></DialogHeader>
        {step === 1 && (
          <div className="space-y-2">
            <Label className="text-xs">Step 1 — Enter your Employee ID or Iqama number</Label>
            <Input value={idNumber} onChange={(e) => setIdNumber(e.target.value)} autoComplete="off" data-testid="confirm-id-input" />
            <p className="text-[11px] text-muted-foreground">It must match your HR record.</p>
          </div>
        )}
        {step >= 2 && review && (
          <div data-testid="confirm-review">
            <Row label="Employee" value={review.employee_name} testid="review-employee" />
            <Row label="Employee No." value={review.employee_number} />
            <Row label="ID / Iqama" value={review.id_masked} />
            <Row label="Branch" value={review.branch_name} />
            <Row label="Payment type" value={review.type_label} testid="review-type" />
            <Row label="Amount" value={`${cur()} ${money(review.amount)}`} testid="review-amount" />
            <Row label="Payment date" value={review.date} />
            <Row label="Salary period" value={review.period} />
            <Row label="Reference" value={review.reference} testid="review-reference" />
            <div className="mt-3 space-y-1">
              <Label className="text-xs">Step 2 — Enter your Salary PIN to confirm you received this payment</Label>
              <Input type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))} data-testid="confirm-pin-input" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {step === 1
            ? <Button onClick={verify} disabled={busy || !idNumber.trim()} data-testid="confirm-next-btn">Next</Button>
            : <Button onClick={confirm} disabled={busy || pin.length < 4} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="confirm-submit-btn">I confirm I received it</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
